import { NotificationsTable } from "./NotificationsTable";
import { Column, Row, Text } from "@/components";
import { Label } from "@/components/ui/label";

import { MIXPANEL_NOTIFICATION_SURFACES } from "@/lib/mixpanel/types";
import { topCandidates } from "@/lib/topCandidates";
import { cn } from "@/lib/utils";
import { NotificationResponse } from "@/types";

// The candidate list, split so a five-star candidate never sits in the same run
// of rows as a four-star one. Sections are priority-sorted inside.
export const NotificationTopCandidatesSections = ({
  hasAnyNotifications,
  isError,
  isPending,
  notifications,
  trackingSurface,
}: {
  hasAnyNotifications: boolean;
  isError: boolean;
  isPending: boolean;
  notifications: NotificationResponse[];
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  /* Helpers */
  const { excellent, finishedStevie, great, recommendations, mentions, other } =
    topCandidates.group(notifications);
  // Rated sections carry their agentic rating colour, mentions their own blue.
  const sections = [
    {
      countClassName: "text-indigo-400",
      headerClassName: "bg-indigo-50",
      label: "Excellent",
      labelClassName: "text-indigo-700",
      notifications: excellent,
    },
    {
      countClassName: "text-rose-400",
      headerClassName: "bg-rose-50",
      label: "Just finished a Stevie call",
      labelClassName: "text-rose-700",
      notifications: finishedStevie,
    },
    {
      countClassName: "text-emerald-400",
      headerClassName: "bg-emerald-50",
      label: "Great",
      labelClassName: "text-emerald-700",
      notifications: great,
    },
    {
      countClassName: "text-primary-700",
      headerClassName: "bg-primary-300",
      label: "Mentions",
      labelClassName: "text-primary-900",
      notifications: mentions,
    },
    {
      countClassName: "text-amber-500",
      headerClassName: "bg-amber-100",
      label: "Recommended to you",
      labelClassName: "text-amber-800",
      notifications: recommendations,
    },
    {
      // Everything that wasn't worth lifting — grey header, ordinary rows.
      countClassName: "text-slate-400",
      headerClassName: "bg-slate-100",
      label: "Other",
      labelClassName: "text-slate-600",
      notifications: other,
    },
  ].filter((section) => section.notifications.length > 0);

  /* Render */
  const render = () => {
    // Loading, error and empty read the same here as in the flat list.
    if (isPending || isError || notifications.length === 0) {
      return (
        <NotificationsTable
          hasAnyNotifications={hasAnyNotifications}
          isCompact
          isError={isError}
          isPending={isPending}
          notifications={notifications}
          trackingSurface={trackingSurface}
        />
      );
    }

    return (
      <Column>
        {sections.map((section) => (
          <Column key={section.label}>
            <Row
              className={cn(
                "sticky top-0 z-10 -mx-4 items-center gap-1.5 bg-white px-4 py-2",
                section.headerClassName,
              )}
            >
              <Label
                className={cn(
                  "text-xs font-semibold uppercase leading-none tracking-wide text-slate-500",
                  section.labelClassName,
                )}
              >
                {section.label}
              </Label>
              <Text
                className={cn(
                  "text-xs font-medium leading-none text-slate-400",
                  section.countClassName,
                )}
              >
                {section.notifications.length}
              </Text>
            </Row>
            <NotificationsTable
              hasAnyNotifications={hasAnyNotifications}
              isCompact
              isError={false}
              isPending={false}
              notifications={section.notifications}
              trackingSurface={trackingSurface}
            />
          </Column>
        ))}
      </Column>
    );
  };

  return render();
};
